import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';
import {
  ISOLVED_CONNECTION_NAME,
  IsolvedParamKeys,
  JUSTWORKS_CONNECTION_NAME,
  LOGIN_HINT_PARAM,
  PAYCHEX_CONNECTION_NAME,
} from '@/types/queryParamKeys';

const useLoginWithConnection = () => {
  /*
    This hook allows us to silently authenticate the user if we know
    they are coming from a specific connection, e.g. trinet, adp, etc.
  */
  const { loginWithRedirect } = useAuth0();
  const connectionRef = useRef<string | null>(null);
  const loginHintRef = useRef<string | null>(null);

  useEffect(() => {
    const queryString = new URLSearchParams(window.location.search);
    const connection = queryString.get('connection');

    if (connection) {
      connectionRef.current = connection;
      queryString.delete('connection');

      if (connection === ISOLVED_CONNECTION_NAME) {
        queryString.delete(IsolvedParamKeys.TenantId);
        queryString.delete(IsolvedParamKeys.ClientId);
        queryString.delete(IsolvedParamKeys.GlobalId);
      }

      if (
        connection === JUSTWORKS_CONNECTION_NAME ||
        connection === PAYCHEX_CONNECTION_NAME
      ) {
        loginHintRef.current = queryString.get(LOGIN_HINT_PARAM);
        queryString.delete(LOGIN_HINT_PARAM);
      }

      const urlPath = window.location.pathname;
      const urlQueryString = queryString.toString()
        ? `?${queryString.toString()}`
        : '';

      window.history.pushState({}, '', `${urlPath}${urlQueryString}`);
    }
  }, []);

  const loginWithConnection = useCallback(() => {
    return Promise.resolve(
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
        ...(connectionRef.current && {
          authorizationParams: {
            // Including the connection value bypasses the login screen and
            // pushes them into the authentication flow for that connection
            connection: connectionRef.current,
            ...(loginHintRef.current && {
              login_hint: loginHintRef.current,
            }),
          },
        }),
      })
    ).catch(error => {
      console.error('Failed to initiate login:', error);
      throw error;
    });
  }, [loginWithRedirect]);

  return { loginWithConnection, connectionRef };
};

export default useLoginWithConnection;
